import React, { useState } from 'react';
import { MegaphoneIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAgentWithSubagents } from '../../hooks/useAgentWithSubagents';
import { useSubscriptionForRenewalDate } from '../../hooks/useSubscriptionForRenewalDate';
import AgentOutputActions from '../../components/Agents/AgentOutputActions';
import { CreditExhaustedModal } from '../../components/Agents/CreditExhaustedModal';
import { CreditUpgradeNudgeModal } from '../../components/Agents/CreditUpgradeNudgeModal';
import SecurityViolationModal from '../../components/Agents/SecurityViolationModal';
import { validateFields } from '../../utils/securityValidator';
import { formatElapsedTime } from '../../utils/timeUtils';
import { 
  AgentPageLayout, 
  AgentFormSection, 
  AgentFormGroup, 
  AgentLabel, 
  AgentInput, 
  AgentSelect,
  AgentButton, 
  AgentError, 
  AgentResult,
  AgentDisclaimer
} from '../../components/Agents/AgentPageLayout';

const SermonBuilder: React.FC = () => {
  const [biblicalReference, setBiblicalReference] = useState<string>('');
  const [sermonType, setSermonType] = useState<string>('expositivo');
  const [sermonTone, setSermonTone] = useState<string>('encorajador');
  const [sermonStyle, setSermonStyle] = useState<string>('narrativo');
  const [targetAudience, setTargetAudience] = useState<string>('igreja');
  const [showSecurityModal, setShowSecurityModal] = useState<boolean>(false);
  
  const {
    isLoading,
    error,
    result,
    steps,
    hasStarted,
    elapsedTime,
    execute,
    reset,
    creditsExhausted,
    creditsInfo,
    setCreditsExhausted,
    canRetry,
    onRetry,
    creditUpgradeNudgeOpen,
    creditUpgradeNudgeUsagePercent,
    dismissCreditUpgradeNudge,
  } = useAgentWithSubagents('construir-sermao');
  const renewalDate = useSubscriptionForRenewalDate();

  const sermonTypes = [
    { value: 'biografico', label: 'Biográfico' },
    { value: 'doutrinario', label: 'Doutrinário' },
    { value: 'expositivo', label: 'Expositivo' },
    { value: 'tematico', label: 'Temático' },
    { value: 'textual', label: 'Textual' },
  ];

  const sermonTones = [
    { value: 'celebrativo', label: 'Celebrativo' },
    { value: 'consolador', label: 'Consolador' },
    { value: 'contemplativo', label: 'Contemplativo' },
    { value: 'corretivo', label: 'Corretivo' },
    { value: 'desafiador', label: 'Desafiador' },
    { value: 'encorajador', label: 'Encorajador' },
    { value: 'evangelizador', label: 'Evangelizador' },
    { value: 'exortativo', label: 'Exortativo' },
    { value: 'inspirador', label: 'Inspirador' },
    { value: 'motivacional', label: 'Motivacional' },
  ];

  const sermonStyles = [
    { value: 'aconselhador', label: 'Aconselhador' },
    { value: 'devocional', label: 'Devocional' },
    { value: 'informativo', label: 'Informativo' },
    { value: 'inquiridor', label: 'Inquiridor' },
    { value: 'instrutivo', label: 'Instrutivo' },
    { value: 'narrativo', label: 'Narrativo' },
    { value: 'persuasivo', label: 'Persuasivo' },
    { value: 'reflexivo', label: 'Reflexivo' },
  ];

  const targetAudiences = [
    { value: 'jovens', label: 'Jovens (18–24 anos)' },
    { value: 'jovens-adultos', label: 'Jovens Adultos (25–34 anos)' },
    { value: 'adultos', label: 'Adultos (35–44 anos)' },
    { value: 'adultos-maduros', label: 'Adultos Maduros (45–54 anos)' },
    { value: 'maturidade', label: 'Maturidade (55–64 anos)' },
    { value: 'melhor-idade', label: 'Melhor Idade (65+)' },
    { value: 'casais', label: 'Casais' },
    { value: 'familias', label: 'Famílias' },
    { value: 'igreja', label: 'Igreja' },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!biblicalReference.trim()) {
      return;
    }

    // Validar segurança antes de enviar
    if (validateFields(biblicalReference)) {
      setShowSecurityModal(true);
      return;
    }

    await execute({
      prompt: `Construa um sermão sólido unindo exegese bíblica, teologia profunda e aplicação prática: ${biblicalReference}`,
      passagem: biblicalReference,
      tipo_sermao: sermonType,
      tom_sermao: sermonTone,
      estilo_sermao: sermonStyle,
      publico_alvo: targetAudience
    });
  };

  const handleReset = () => {
    setBiblicalReference('');
    setSermonType('expositivo');
    setSermonTone('encorajador');
    setSermonStyle('narrativo');
    setTargetAudience('igreja');
    reset();
  };

  return (
    <AgentPageLayout
      icon={<MegaphoneIcon className="w-6 h-6 text-secondary" />}
      title="Construir Sermão"
      subtitle="Construa sermões sólidos unindo exegese bíblica, teologia profunda e aplicação prática."
    >
      <AgentFormSection>
        <form onSubmit={handleSubmit} className="space-y-6">
          <AgentFormGroup>
            <AgentLabel htmlFor="biblicalReference">Referência Bíblica ou Tema</AgentLabel>
            <AgentInput
              id="biblicalReference"
              type="text"
              value={biblicalReference}
              onChange={(e) => setBiblicalReference(e.target.value)}
              placeholder="Ex: João 3:16 ou O Amor de Deus"
              disabled={isLoading}
            />
          </AgentFormGroup>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AgentFormGroup>
              <AgentLabel htmlFor="sermonType">Tipo de Sermão</AgentLabel>
              <AgentSelect
                id="sermonType"
                value={sermonType}
                onChange={(e) => setSermonType(e.target.value)}
                disabled={isLoading}
              >
                {sermonTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </AgentSelect>
            </AgentFormGroup>

            <AgentFormGroup>
              <AgentLabel htmlFor="sermonTone">Tom do Sermão</AgentLabel>
              <AgentSelect
                id="sermonTone"
                value={sermonTone}
                onChange={(e) => setSermonTone(e.target.value)}
                disabled={isLoading}
              >
                {sermonTones.map((tone) => (
                  <option key={tone.value} value={tone.value}>
                    {tone.label}
                  </option>
                ))}
              </AgentSelect>
            </AgentFormGroup>

            <AgentFormGroup>
              <AgentLabel htmlFor="sermonStyle">Estilo do Sermão</AgentLabel>
              <AgentSelect
                id="sermonStyle"
                value={sermonStyle}
                onChange={(e) => setSermonStyle(e.target.value)}
                disabled={isLoading}
              >
                {sermonStyles.map((style) => (
                  <option key={style.value} value={style.value}>
                    {style.label}
                  </option>
                ))}
              </AgentSelect>
            </AgentFormGroup>

            <AgentFormGroup>
              <AgentLabel htmlFor="targetAudience">Público-alvo</AgentLabel>
              <AgentSelect
                id="targetAudience"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                disabled={isLoading}
              >
                {targetAudiences.map((audience) => (
                  <option key={audience.value} value={audience.value}>
                    {audience.label}
                  </option>
                ))}
              </AgentSelect>
            </AgentFormGroup>
          </div>

          <div className="flex justify-end">
            <AgentButton type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Construindo Sermão...</span>
                </>
              ) : (
                <>
                  <MegaphoneIcon className="w-5 h-5" />
                  <span>Construir Sermão</span>
                </>
              )}
            </AgentButton>
          </div>
        </form>

        {error && <AgentError message={error} onRetry={canRetry ? onRetry : undefined} />}
      </AgentFormSection>

      {hasStarted && (
        <section className="mt-8">
          <div className="bg-card border border-border rounded-lg p-6">
            {isLoading && (
              <div className="flex flex-col items-center justify-center gap-4 py-8">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😇</span>
                  <h3 className="text-lg font-semibold text-foreground">
                    Enquanto trabalhamos, eleve seu pensamento e descanse na presença de Deus.
                  </h3>
                </div>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
                {steps.length > 0 && steps[0]?.title && (
                  <p className="text-sm text-muted-foreground">{steps[0].title}</p>
                )}
                {elapsedTime > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Tempo de execução: {formatElapsedTime(elapsedTime)}
                  </p>
                )}
              </div>
            )}
            
            {result && result.trim().length > 0 && (
              <>
                <div className="mt-6">
                  <AgentResult title="Sermão Criado">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {result}
                    </ReactMarkdown>
                  </AgentResult>
                </div>
                <AgentDisclaimer />
                <div className="mt-6 flex items-center justify-end gap-3">
                  <AgentOutputActions
                    content={result}
                    agentName="Construir Sermão"
                    agentCode="construir-sermao"
                    reference={biblicalReference}
                  />
                  <AgentButton
                    type="button"
                    onClick={handleReset}
                    variant="secondary"
                    aria-label="Reiniciar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                    <span>Reiniciar</span>
                  </AgentButton>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      <SecurityViolationModal
        open={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        onRestart={handleReset}
      />
      <CreditUpgradeNudgeModal
        isOpen={creditUpgradeNudgeOpen}
        onClose={dismissCreditUpgradeNudge}
        usagePercent={creditUpgradeNudgeUsagePercent}
      />
      {creditsExhausted && (
        <CreditExhaustedModal
          isOpen={creditsExhausted}
          onClose={() => setCreditsExhausted(false)}
          renewalDate={renewalDate}
          creditsInfo={creditsInfo || undefined}
        />
      )}
    </AgentPageLayout>
  );
};

export default SermonBuilder;
